<template>
  <div style="padding-top: 9.5rem" class="pb-5">
    <div class="details reservationoverview">
      <div class="details__title px-5 mt-0" v-if="reservation"></div>
      <div class="container">
        <h1 class="title is-3">{{ $t("thankYouPage.PageHeading") }}</h1>
        <div class="columns is-multiline">
          <div class="column is-full has-text-centered" v-if="isLoading">
            <h3 class="title is-3">Loading your reservation...</h3>
            <br />
            <img width="50" height="60" src="@/assets/images/loader.svg" />
          </div>
          <div class="column is-full has-text-centered" v-else-if="error">
            <p class="has-text-danger">{{ error }}</p>
          </div>

          <div class="column is-full" v-else-if="reservation">
            <div class="section full-width">
              <h5 class="title is-5">
                🏨 {{ $t("thankYouPage.HotelHeading") }}
              </h5>
              <p>
                <span class="u-weight--bold"
                  >{{ $t("thankYouPage.HotelHeading") }}:</span
                >
                {{ reservation.hotel.name }}
              </p>
              <p>
                <span class="u-weight--bold">{{ $t("address") }}:</span>
                {{ reservation.hotel.street }},
                {{ reservation.hotel.postalCode }} {{ reservation.hotel.city }},
                {{ reservation.hotel.country }}
              </p>
            </div>
            <br />
            <div class="columns is-multiline">
              <div class="column is-half-tablet is-full-mobile">
                <div class="section">
                  <h5 class="title is-5">
                    📅 {{ $t("thankYouPage.BookingHeading") }}
                  </h5>
                  <p>
                    <span class="u-weight--bold">{{
                      $t("thankYouPage.CheckInDate")
                    }}</span>
                    {{ formatDateLabel(reservation.selectedDates[0]) }}
                  </p>
                  <p>
                    <span class="u-weight--bold">{{
                      $t("thankYouPage.CheckInTime")
                    }}</span>
                    {{ reservation.hotel.checkInStartTime }} -
                    {{ reservation.hotel.checkInEndTime }}
                  </p>

                  <br />

                  <p>
                    <span class="u-weight--bold">{{
                      $t("thankYouPage.CheckOutDate")
                    }}</span>
                    {{
                      formatDateLabel(
                        getCheckoutDate(
                          reservation.selectedDates[0],
                          reservation.selectedDates.length
                        )
                      )
                    }}
                  </p>

                  <p>
                    <span class="u-weight--bold">{{
                      $t("thankYouPage.CheckOutTime")
                    }}</span>
                    {{ reservation.hotel.checkOutTime }}
                  </p>
                </div>
              </div>
              <div class="column is-half-tablet is-full-mobile">
                <div class="section">
                  <h5 class="title is-5">
                    💰 {{ $t("thankYouPage.PaymentHeading") }}
                  </h5>
                  <p>
                    <span class="u-weight--bold">Refundable:</span>
                    {{ offerIsRefundable ? "✅" : "❌" }}
                  </p>
                  <p>
                    <span class="u-weight--bold">{{
                      $t("thankYouPage.Breakfast")
                    }}</span>
                    {{ offerIsBreakfastIncluded ? "✅" : "❌" }}
                  </p>
                  <p>
                    <span class="u-weight--bold">{{
                      $t("thankYouPage.Price")
                    }}</span>
                    {{ formatPriceLabel }}
                  </p>
                </div>
              </div>
              <div
                class="column is-full has-text-centered mt-5"
                v-if="reservation"
              >
                <a
                  href="/reservation-overview"
                  class="c-button c-button--secondary"
                >
                  {{ $t("thankYouPage.CompleteBooking") }}
                </a>
              </div>
            </div>
            <br />
            <div class="column is-full">
              <div class="section full-width">
                <h5 class="title is-5">
                  📩 {{ $t("thankYouPage.InfoHeading") }}
                </h5>
                <p>
                  {{ $t("thankYouPage.InfoParagraph1") }}
                </p>
                <ul class="booking-links">
                  <li v-if="enableFlightRequests">
                    <a href="/flightform"
                      >✈️ {{ $t("thankYouPage.FlightsHrefs") }}</a
                    >
                  </li>
                  <li v-if="enableTrainRequests">
                    <a href="/trainform"
                      >🚆 {{ $t("transportThankYouPage.TrainHref") }}</a
                    >
                  </li>
                  <li v-if="enableCarRequests">
                    <a href="/carform"
                      >🚗 {{ $t("transportThankYouPage.CarHref") }}</a
                    >
                  </li>
                  <li v-if="enableMeetingRequests">
                    <a href="/meetingroomreservation"
                      >🏢 {{ $t("thankYouPage.MeetingHref") }}</a
                    >
                  </li>
                  <li>
                    <a href="/">🏨 {{ $t("thankYouPage.HotelsHrefs") }}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="column is-full has-text-centered" v-else>
            {{ $t("thankYouPage.ReservationError") }}}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReservationHelper from "@/helpers/reservation.overview.helper";
import CommonHelper from "@/helpers/common.helper.js";
import moment from "moment";

export default {
  computed: {
    offerIsRefundable() {
      return !!(
        this.reservation?.selectedOffer?.isRefundable ??
        this.reservation?.isRefundable ??
        this.reservation?.refundable ??
        false
      );
    },
    offerIsBreakfastIncluded() {
      return !!(
        this.reservation?.selectedOffer?.isBreakfastIncluded ??
        this.reservation?.isBreakfastIncluded ??
        this.reservation?.breakfastIncluded ??
        false
      );
    },
    formatPriceLabel() {
      const price = this.reservation?.selectedOffer?.totalPrice;
      return price != null
        ? `€${Number(price).toFixed(2)}`
        : this.$t("thankYouPage.NotAvailable");
    }
  },
  data() {
    return {
      reservation: null,
      isLoading: true,
      error: null,
      email: "zakelijk@lookingforbooking.com",
      enableMeetingRequests: false,
      enableFlightRequests: false,
      enableTrainRequests: false,
      enableCarRequests: false
    };
  },
  watch: {
    $route() {
      this.checkLoginStatus();
    }
  },

  async created() {
    moment.locale(this.languageIso);

    await this.fetchReservation();
    await this.checkLoginStatus();

    if (this.isLoggedIn) {
      this.injectCustomCss();
      let companyInfo = await CommonHelper.getCompanyInfo();
      this.fullName = companyInfo?.fullName ?? "";
      this.enableMeetingRequests = companyInfo?.enableMeetingRequests ?? true;
      this.enableFlightRequests = companyInfo?.enableFlightRequests ?? true;
      this.enableTrainRequests = companyInfo?.enableTrainRequests ?? true;
      this.enableCarRequests = companyInfo?.enableCarRequests ?? true;
    }
  },
  methods: {
    async fetchReservation() {
      try {
        this.isLoading = true;
        this.error = null;

        const response = await ReservationHelper.getReservation();

        // console.log(response);

        if (
          response &&
          response.reservationList &&
          response.reservationList.length > 0
        ) {
          this.reservation = response.reservationList[0];
        } else {
          this.error = this.$t("thankYouPage.ReservationError");
        }
      } catch (err) {
        this.error = "Failed to load reservation. Please try again.";
        console.error("Error fetching reservation:", err);
      } finally {
        this.isLoading = false;
      }
    },
    getCheckoutDate(checkin, nights) {
      return moment(checkin).add(nights, "days");
    },
    formatDate(date) {
      return moment(date).format("MMMM Do, YYYY");
    },
    async checkLoginStatus() {
      let userData = CommonHelper.isLoggedIn();
      if (userData) {
        this.isLoggedIn = true;
        this.userName = userData.userName;
        let companyInfo = await CommonHelper.getCompanyInfo();

        this.enableMeetingRequests = companyInfo.enableMeetingRequests ?? true;
        this.enableFlightRequests = companyInfo.enableFlightRequests ?? true;
        this.enableTrainRequests = companyInfo.enableTrainRequests ?? true;
        this.enableCarRequests = companyInfo?.enableCarRequests ?? true;
      } else {
        this.isLoggedIn = false;
      }
    }
  }
};
</script>

<style scoped>
.details__title {
  background: url("@/assets/images/hotel-confirmation-bg.jpg") center center
    no-repeat;
  background-size: cover;
  height: 26rem;
  text-align: center;
  padding: 40px 20px;
  color: white;
  border-radius: 10px;
}

.details__title h1 {
  font-size: 28px;
  font-weight: bold;
  color: #fff;
  margin-bottom: 10px;
  margin-top: 17.5rem;
}

.details__title .subtitle {
  font-size: 18px;
  color: #ddd;
}

.details.reservationoverview {
  font-family: "Arial", sans-serif;
  background-color: #ffffff;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 900px;
  margin: 0 auto;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  align-items: start;
}

.full-width {
  grid-column: span 2;
}

.section {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out;
}

.section:hover {
  transform: translateY(-2px);
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}

.details__content__box p {
  font-size: 16px;
  color: #444;
  line-height: 1.6;
  margin-bottom: 8px;
}

.details__content__box strong {
  color: #222;
  font-weight: 600;
}

@media (max-width: 768px) {
  .grid-container {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: stretch;
    width: 100%;
  }

  .section {
    width: 92%;
    max-width: 400px;
    margin: 0 auto;
    padding: 15px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .details__content__box {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .full-width {
    width: 92%;
    max-width: 400px;
    margin: 0 auto 10px auto;
  }

  .details__title {
    padding: 20px 10px;
  }

  .details__title h1 {
    font-size: 22px;
  }

  .section-title {
    font-size: 18px;
  }

  .details__content__box p {
    font-size: 14px;
  }
}
.booking-links {
  list-style-type: none;
  padding: 0;
  margin-top: 10px;
}

.booking-links li {
  margin-bottom: 8px;
}
.booking-links a {
  color: #007bff;
  text-decoration: none;
  font-weight: bold;
}

.booking-links a:hover {
  text-decoration: underline;
}
.complete-trip-button {
  background-color: #28a745;
  color: white;
  font-size: 18px;
  font-weight: bold;
  padding: 12px 20px;
  border-radius: 10px;
  text-decoration: none;
  display: inline-block;
  margin-top: 20px;
  transition: background 0.3s ease-in-out;
}
.complete-trip-button:hover {
  background-color: #218838;
}
</style>
